import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAppDispatch } from '@/app/hooks';
import { paths } from '@/config/paths';
import type { AnalyticsFilter, AnalyticsQuery, SortDirection } from '@/shared/api';
import { ErrorState, ExportButton, Loading } from '@/shared/ui';
import { useAnalyticsRecords } from '../api/analyticsApi';
import { loadRecords, RECORD_EXPORT_LIMIT, recordExportColumns } from '../model/exportRecords';
import { validateFilter } from '../model/filters';
import { findColumn, timeSortColumn, type AnalyticsObject } from '../model/schema';
import { buildAnalyticsSearch } from '../model/useAnalyticsSearchParams';
import { useRecordOpener } from '../model/useRecordOpener';
import { AnalyticsTable } from './AnalyticsTable';
import { Pager } from './Pager';
import { RecordDrawer } from './RecordDrawer';

const PAGE_SIZE = 25;

/** Sync bookkeeping columns, hidden in related tables (the record page and full list show them). */
const BOOKKEEPING = new Set(
  [
    'AdditionalFieldsJson',
    'Metadata',
    'D365Id',
    'HubSpotId',
    'OaaId',
    'LiveMode',
    'LastSyncedAt',
    'MissingFromSourceSince',
    'DefaultTaxRateIdsJson',
    'DiscountIdsJson',
  ].map((name) => name.toLowerCase()),
);

export interface RelatedRecordsPanelProps {
  object: AnalyticsObject;
  /** Conditions that select the related rows, e.g. `SubscriptionId:eq:sub_123`. */
  filters: AnalyticsFilter[];
  /** Accessible name of the table. */
  label: string;
  sort?: { sortBy: string; sortDirection: SortDirection };
  /** Column names to show, in order (default: all but sync bookkeeping). */
  columns?: string[];
  /** Reports the number of related rows once loaded (shown on the tab). */
  onCount?: (count: number) => void;
}

/**
 * Related rows of a record: table, paging and a link to the full list in the data explorer.
 * Rendered only for the selected tab, so unindexed tables are scanned on demand.
 */
export function RelatedRecordsPanel({
  object,
  filters,
  label,
  sort,
  columns,
  onCount,
}: RelatedRecordsPanelProps) {
  const [pageNumber, setPageNumber] = useState(1);
  // A column header the viewer selected overrides the panel's default order.
  const [sorted, setSorted] = useState<{ sortBy: string; sortDirection: SortDirection }>();
  const record = useRecordOpener(object);
  const dispatch = useAppDispatch();
  const newest = sort ? undefined : timeSortColumn(object);
  const order =
    sorted ?? sort ?? (newest ? { sortBy: newest, sortDirection: 'desc' as const } : undefined);
  /** Ascending first; the same column again reverses the order. */
  const toggleSort = (column: string) => {
    const same = order?.sortBy.toLowerCase() === column.toLowerCase();
    setSorted({
      sortBy: column,
      sortDirection: same && order.sortDirection === 'asc' ? 'desc' : 'asc',
    });
    setPageNumber(1);
  };
  // The columns that link the rows to the record (e.g. AppId on an app) repeat its own ID.
  const linking = new Set(
    filters.filter((f) => f.operator === 'eq').map((f) => f.column.toLowerCase()),
  );
  const visible = columns
    ? columns.flatMap((name) => findColumn(object, name) ?? [])
    : object.columns.filter(
        (column) =>
          !BOOKKEEPING.has(column.name.toLowerCase()) && !linking.has(column.name.toLowerCase()),
      );
  // e.g. a free-text product app ID cannot match a uniqueidentifier AppId column.
  const inapplicable = filters.map((filter) => validateFilter(object, filter)).find(Boolean);
  const query: AnalyticsQuery = {
    pageNumber,
    pageSize: PAGE_SIZE,
    sortDirection: order?.sortDirection ?? 'asc',
    ...(order ? { sortBy: order.sortBy } : {}),
    filters,
  };
  const { data, error, isLoading, isFetching, refetch } = useAnalyticsRecords(
    object,
    inapplicable ? skipToken : query,
  );
  const total = data?.totalCount;

  useEffect(() => {
    if (total !== undefined) onCount?.(total);
  }, [total, onCount]);

  if (inapplicable) {
    return (
      <p className="empty">
        Not applicable: this record&apos;s ID cannot match {object.title} ({inapplicable})
      </p>
    );
  }
  return (
    <>
      {isLoading && (
        <div className="record__note">
          <Loading />
        </div>
      )}
      {error && (
        <div className="record__note">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      )}
      {data && (
        <div aria-busy={isFetching}>
          <AnalyticsTable
            object={object}
            caption={label}
            columns={visible}
            rows={data.items}
            rowOffset={(data.pageNumber - 1) * data.pageSize}
            sort={order ?? { sortDirection: 'asc' }}
            onToggleSort={toggleSort}
            onOpenRow={record.open}
          />
          <footer className="card__foot">
            <div className="toolbar">
              {data.totalPages > 1 && (
                <Pager
                  pageNumber={data.pageNumber}
                  totalPages={data.totalPages}
                  totalCount={data.totalCount}
                  onPageChange={setPageNumber}
                  disabled={isFetching}
                />
              )}
              <ExportButton
                name={label}
                columns={recordExportColumns(visible)}
                rows={() => loadRecords(dispatch, object, query)}
                total={data.totalCount}
                limit={RECORD_EXPORT_LIMIT}
                disabled={data.totalCount === 0}
              />
              <Link
                className="btn btn--link"
                to={{
                  pathname: paths.analytics.object(object.key),
                  search: buildAnalyticsSearch(filters),
                }}
              >
                Open in {object.title}
              </Link>
            </div>
          </footer>
          <RecordDrawer object={object} row={record.row} onClose={record.close} />
        </div>
      )}
    </>
  );
}
